// A table's columns as someone set them in its Columns menu (`column-order.ts`): shown or hidden,
// and in order — a row dragged above or below another, or moved with its buttons; frozen columns
// never move. Kept per person per table in this browser; the menu's Reset puts the default back.
//
// The drag is the pointer's, not the browser's (its drag image showed a block of the list): the row
// held follows the pointer, the others slide to open its place, and a line marks where it lands.

import { useEffect, useRef, useState } from "react";
import type { CSSProperties, MouseEvent, PointerEvent } from "react";
import { moveColumn, orderColumns } from "./column-order";

const read = (key: string): string[] => {
  try {
    const raw = localStorage.getItem(key);
    const v: unknown = raw ? JSON.parse(raw) : [];
    return Array.isArray(v) ? v.filter((x): x is string => typeof x === "string") : [];
  } catch {
    return [];
  }
};
const write = (key: string, order: readonly string[]) => {
  try {
    if (order.length) localStorage.setItem(key, JSON.stringify(order));
    else localStorage.removeItem(key);
  } catch {
    // Private window: kept for this page only.
  }
};

/** A press moves this far before it is a drag; less is a click (the row's checkbox). */
const START = 4;
/** The list scrolls by itself while a row is held this near its top or bottom edge. */
const EDGE = 32;
const SCROLL_STEP = 8;

/** A row pressed, then dragged: where it started, and the rows it moves among in the list's own offsets. */
interface Held {
  id: string;
  ids: readonly string[];
  startY: number;
  clientY: number;
  dragging: boolean;
  /** The movable rows shown (a search may hide some), as laid out: transforms and scrolling leave these alone. */
  rows: { id: string; top: number; h: number }[];
  from: number;
  raf: number;
}

/** What a render shows of a drag: the row held, how far it moved, and the place it would land. */
interface Drag {
  id: string;
  dy: number;
  slot: number;
  from: number;
  h: number;
  rows: string[];
}

/** A page's column order (`key`: where it is kept), and the props that make the menu's rows movable. */
export function useColumnOrder(key: string) {
  const [order, setOrder] = useState<string[]>(() => read(key));
  const [drag, setDrag] = useState<Drag | null>(null);
  /** The menu's scrolling list: `position: relative`, so the rows' offsets are in it. */
  const listRef = useRef<HTMLDivElement>(null);
  const held = useRef<Held | null>(null);
  const shown = useRef<Drag | null>(null);
  /** The click a drag ends with toggles nothing. */
  const swallowClick = useRef(false);
  const onKey = useRef<(e: KeyboardEvent) => void>(() => {});
  const save = (next: string[]) => {
    setOrder(next);
    write(key, next);
  };

  const contentY = (clientY: number) => {
    const el = listRef.current;
    return el ? clientY - el.getBoundingClientRect().top + el.scrollTop : clientY;
  };
  /** How far the row moved (kept between the first and last rows), and how many others it is below. */
  const place = (h: Held) => {
    const d = h.rows[h.from]!;
    const first = h.rows[0]!;
    const last = h.rows[h.rows.length - 1]!;
    const dy = Math.min(Math.max(contentY(h.clientY) - h.startY, first.top - d.top), last.top + last.h - d.top - d.h);
    const center = d.top + d.h / 2 + dy;
    const slot = h.rows.filter((r, i) => i !== h.from && r.top + r.h / 2 < center).length;
    return { dy, slot, d };
  };
  const show = (h: Held) => {
    const { dy, slot, d } = place(h);
    const was = shown.current;
    if (was && was.dy === dy && was.slot === slot) return;
    shown.current = { id: h.id, dy, slot, from: h.from, h: d.h, rows: h.rows.map((r) => r.id) };
    setDrag(shown.current);
  };
  /** Each frame of a drag: the list scrolls near its edges, and the place is worked out again. */
  const tick = () => {
    const h = held.current;
    const el = listRef.current;
    if (!h?.dragging || !el) return;
    const r = el.getBoundingClientRect();
    if (h.clientY < r.top + EDGE) el.scrollTop -= SCROLL_STEP;
    else if (h.clientY > r.bottom - EDGE) el.scrollTop += SCROLL_STEP;
    show(h);
    h.raf = requestAnimationFrame(tick);
  };
  const stopListening = (h: Held) => {
    cancelAnimationFrame(h.raf);
    window.removeEventListener("keydown", onKey.current, true);
    document.body.style.cursor = "";
  };
  const end = (drop: boolean) => {
    const h = held.current;
    held.current = null;
    if (!h?.dragging) return;
    stopListening(h);
    swallowClick.current = true;
    shown.current = null;
    setDrag(null);
    if (!drop) return;
    const { slot } = place(h);
    if (slot === h.from) return;
    const others = h.rows.filter((r) => r.id !== h.id);
    const below = slot >= others.length;
    save(moveColumn(h.ids, h.id, others[below ? others.length - 1 : slot]!.id, below));
  };
  const begin = (h: Held) => {
    const el = listRef.current;
    if (!el) return;
    h.rows = [...el.querySelectorAll<HTMLElement>("[data-col-id]")].map((r) => ({
      id: r.dataset.colId!,
      top: r.offsetTop,
      h: r.offsetHeight,
    }));
    h.from = h.rows.findIndex((r) => r.id === h.id);
    if (h.from < 0) return;
    h.dragging = true;
    document.body.style.cursor = "grabbing";
    onKey.current = (e) => {
      if (e.key !== "Escape") return;
      // Escape puts the row back; the menu stays open.
      e.preventDefault();
      e.stopPropagation();
      end(false);
    };
    window.addEventListener("keydown", onKey.current, true);
    show(h);
    h.raf = requestAnimationFrame(tick);
  };
  // The menu closed mid-drag: nothing left listening, the cursor as it was.
  useEffect(
    () => () => {
      if (held.current?.dragging) stopListening(held.current);
    },
    [],
  );

  /** How a row is drawn during a drag: the one held follows the pointer, the others make room. */
  const drawn = (id: string): { style?: CSSProperties; drop?: "above" | "below"; dragging?: boolean } => {
    if (!drag) return {};
    if (id === drag.id)
      return {
        dragging: true,
        style: { transform: `translateY(${drag.dy}px) scale(1.02)`, position: "relative", zIndex: 10 },
      };
    const j = drag.rows.indexOf(id);
    if (j < 0) return {};
    const shift = j < drag.from ? (drag.slot <= j ? drag.h : 0) : drag.slot >= j ? -drag.h : 0;
    const others = drag.rows.filter((r) => r !== drag.id);
    const drop =
      others[drag.slot] === id ? "above" : drag.slot === others.length && others.at(-1) === id ? "below" : undefined;
    return { drop, style: { transform: `translateY(${shift}px)`, transition: "transform 150ms ease" } };
  };

  return {
    /** The movable columns' ids as this person put them; [] is the default. */
    order,
    reset: () => save([]),
    /** The Columns menu's scrolling list. */
    listRef,
    /**
     * A menu row's drag props: pressed and moved, it follows the pointer and lands where the line
     * shows. `ids`: every movable column in its order now, hidden ones too.
     */
    row: (id: string, ids: readonly string[]) => {
      const d = drawn(id);
      return {
        "data-col-id": id,
        "data-drop": d.drop,
        "data-dragging": d.dragging ? "" : undefined,
        style: d.style,
        onPointerDown: (e: PointerEvent<HTMLElement>) => {
          swallowClick.current = false;
          if (e.button !== 0 || held.current) return;
          // The checkbox and the buttons keep their own clicks.
          if ((e.target as HTMLElement).closest("button, input, [role=checkbox]")) return;
          e.currentTarget.setPointerCapture(e.pointerId);
          // A mouse press selects no text.
          if (e.pointerType === "mouse") e.preventDefault();
          held.current = {
            id,
            ids,
            startY: contentY(e.clientY),
            clientY: e.clientY,
            dragging: false,
            rows: [],
            from: -1,
            raf: 0,
          };
        },
        onPointerMove: (e: PointerEvent<HTMLElement>) => {
          const h = held.current;
          if (h?.id !== id) return;
          h.clientY = e.clientY;
          if (!h.dragging && Math.abs(contentY(e.clientY) - h.startY) >= START) begin(h);
        },
        onPointerUp: () => end(true),
        onPointerCancel: () => end(false),
        onClickCapture: (e: MouseEvent<HTMLElement>) => {
          if (!swallowClick.current) return;
          swallowClick.current = false;
          e.preventDefault();
          e.stopPropagation();
        },
      };
    },
    /** Up or down one place: the keyboard's way (and a click's). */
    step: (id: string, ids: readonly string[], by: -1 | 1) => {
      const i = ids.indexOf(id);
      const j = i + by;
      if (i < 0 || j < 0 || j >= ids.length) return;
      save(moveColumn(ids, id, ids[j]!, by === 1));
    },
    /** First or last of the movable columns. */
    toEdge: (id: string, ids: readonly string[], edge: "top" | "bottom") => {
      if (!ids.length || ids[edge === "top" ? 0 : ids.length - 1] === id) return;
      save(moveColumn(ids, id, ids[edge === "top" ? 0 : ids.length - 1]!, edge === "bottom"));
    },
  };
}

export type ColumnOrder = ReturnType<typeof useColumnOrder>;

/**
 * A table's order and hidden columns (the Shipping schedule, the Ship log, the System log,
 * Pre-route), kept per person under `routing.*.<table>`. `hiddenByDefault` columns stay hidden
 * until the person shows them (kept apart, `routing.shown.<table>`), so a column added hidden
 * reaches those who already saved a choice hidden too.
 */
export function useTableColumns<T extends { id: string; sticky?: boolean }>(
  table: string,
  all: readonly T[],
  hiddenByDefault: readonly string[] = [],
) {
  const order = useColumnOrder(`routing.order.${table}`);
  const [own, setOwn] = useState<string[]>(() => read(`routing.hidden.${table}`));
  const [shownDefaults, setShownDefaults] = useState<string[]>(() => read(`routing.shown.${table}`));
  const hidden = [...new Set([...own, ...hiddenByDefault.filter((id) => !shownDefaults.includes(id))])];
  const ordered = orderColumns(all, order.order);
  const save = (nextOwn: string[], nextShown: string[]) => {
    setOwn(nextOwn);
    setShownDefaults(nextShown);
    write(`routing.hidden.${table}`, nextOwn);
    write(`routing.shown.${table}`, nextShown);
  };
  return {
    order,
    hidden,
    /** Every column in this person's order, for the menu. */
    ordered,
    /** The columns the table shows, in order. */
    shown: ordered.filter((c) => c.sticky || !hidden.includes(c.id)),
    setHidden: (next: string[]) =>
      save(
        next.filter((id) => !hiddenByDefault.includes(id)),
        hiddenByDefault.filter((id) => !next.includes(id)),
      ),
    reset: () => {
      order.reset();
      save([], []);
    },
  };
}
